import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Grid,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    Divider,
    Alert,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    CircularProgress,
    Pagination,
    Chip,
} from '@mui/material';
import { toast } from 'react-toastify';
import AppDialogTitle from '../../../../ui/appDialogTitle';
import LensConfigurationService from '../../../../../service/lensConfigurationService';
import { ValidDataService } from '../../../../../service/validDataService';
import { isLocalDev } from '../../../../../utils/environment';
import { formatAmountForDisplay } from '../../../../../utils';
import { Colors } from '../../../../../styles/theme';

const INITIAL_EYE_CONFIG = {
    manufacturerId: '',
    lensModelId: '',
    lensMaterialId: '',
    lensIndexId: '',
    treatmentIds: [],
    availableTreatments: [],
    pricing: null,
    labInstructions: '',
};

const PAGE_SIZE = 15; // Show 15 combos per page

const LAB_INSTRUCTION_TOKENS = [
    'Tint: Grey',
    'Tint: Brown',
    'Tint: Green',
    'Gradient',
    'Solid',
    'Match existing',
    'Edge: Polished',
    'Edge: Bevelled',
    'Edge: Rolled',
    'Rush order',
    'Redo'
];

const LENS_TYPE_MAP = {
    single_vision: { code: 'SV', label: 'Single Vision', color: 'success' },
    progressive: { code: 'P', label: 'Progressive', color: 'primary' },
    bifocal: { code: 'BF', label: 'Bifocal', color: 'secondary' },
    trifocal: { code: 'TF', label: 'Trifocal', color: 'warning' },
    aphakic: { code: 'AP', label: 'Aphakic', color: 'error' },
    vocational: { code: 'VOC', label: 'Vocational', color: 'info' },
};

/**
 * Filterable pricing matrix grid component with pagination
 * Displays material × index combinations for a manufacturer with pricing
 */
function LensPricingMatrix({ combos, selectedCombo, onSelectCombo, filterText, onFilterChange, lensTypeFilter, onLensTypeFilterChange, sortBy, onSortChange, currentPage, onPageChange }) {
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('modelName');

    const handleSort = (field) => {
        const isAsc = orderBy === field && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(field);
        onSortChange(field, isAsc ? 'desc' : 'asc');
    };

    // Derive available lens types from current combos for the dropdown
    const availableLensTypes = useMemo(() => {
        const types = new Set(combos.map(c => c.lensType).filter(Boolean));
        return [...types];
    }, [combos]);

    // Filter combos by text and lens type
    const filteredCombos = useMemo(() => {
        let result = combos;
        if (lensTypeFilter) {
            result = result.filter(c => c.lensType === lensTypeFilter);
        }
        if (filterText) {
            const lowerFilter = filterText.toLowerCase();
            result = result.filter(c =>
                c.modelName.toLowerCase().includes(lowerFilter) ||
                c.materialName.toLowerCase().includes(lowerFilter)
            );
        }
        return result;
    }, [combos, filterText, lensTypeFilter]);

    // Sort combos
    const sortedCombos = useMemo(() => {
        if (!filteredCombos) return [];
        const sorted = [...filteredCombos];
        sorted.sort((a, b) => {
            let aVal, bVal;
            switch (orderBy) {
                case 'modelName':
                    aVal = a.modelName;
                    bVal = b.modelName;
                    break;
                case 'materialName':
                    aVal = a.materialName;
                    bVal = b.materialName;
                    break;
                case 'indexValue':
                    aVal = parseFloat(a.indexValue);
                    bVal = parseFloat(b.indexValue);
                    break;
                case 'baseRetailPrice':
                    aVal = a.baseRetailPrice;
                    bVal = b.baseRetailPrice;
                    break;
                case 'treatmentCount':
                    aVal = a.treatmentCount;
                    bVal = b.treatmentCount;
                    break;
                default:
                    return 0;
            }
            if (typeof aVal === 'string') {
                return order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            return order === 'asc' ? aVal - bVal : bVal - aVal;
        });
        return sorted;
    }, [filteredCombos, order, orderBy]);

    const isSelected = (combo) => selectedCombo?.pricingMatrixId === combo.pricingMatrixId;

    // Calculate pagination
    const totalPages = Math.ceil(sortedCombos.length / PAGE_SIZE);
    const startIdx = (currentPage - 1) * PAGE_SIZE;
    const paginatedCombos = sortedCombos.slice(startIdx, startIdx + PAGE_SIZE);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Filter by model name or material..."
                    value={filterText}
                    onChange={(e) => {
                        onFilterChange(e.target.value);
                        onPageChange(1);
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Lens Type</InputLabel>
                    <Select
                        value={lensTypeFilter}
                        label="Lens Type"
                        onChange={(e) => {
                            onLensTypeFilterChange(e.target.value);
                            onPageChange(1);
                        }}
                    >
                        <MenuItem value=""><em>All types</em></MenuItem>
                        {availableLensTypes.map(type => (
                            <MenuItem key={type} value={type}>
                                <Chip
                                    label={LENS_TYPE_MAP[type]?.code || type}
                                    size="small"
                                    color={LENS_TYPE_MAP[type]?.color || 'default'}
                                    variant="outlined"
                                    sx={{ mr: 1, fontWeight: 700 }}
                                />
                                {LENS_TYPE_MAP[type]?.label || type}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {sortedCombos.length === 0 ? (
                <Alert severity="info">
                    {filterText || lensTypeFilter ? 'No combos match your filter' : 'No lens configurations available for this manufacturer'}
                </Alert>
            ) : (
                <>
                    <TableContainer component={Paper} sx={{ overflowY: 'scroll', maxHeight: '500px' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow sx={{ bgcolor: Colors.lightGray }}>
                                    <TableCell align="center" sx={{ width: '40px' }}>Select</TableCell>
                                    <TableCell sx={{ width: '70px' }}>Type</TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'modelName'}
                                            direction={orderBy === 'modelName' ? order : 'asc'}
                                            onClick={() => handleSort('modelName')}
                                        >
                                            Model
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'materialName'}
                                            direction={orderBy === 'materialName' ? order : 'asc'}
                                            onClick={() => handleSort('materialName')}
                                        >
                                            Material
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'indexValue'}
                                            direction={orderBy === 'indexValue' ? order : 'asc'}
                                            onClick={() => handleSort('indexValue')}
                                        >
                                            Index
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={orderBy === 'baseRetailPrice'}
                                            direction={orderBy === 'baseRetailPrice' ? order : 'asc'}
                                            onClick={() => handleSort('baseRetailPrice')}
                                        >
                                            Retail
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="center">Treatments</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedCombos.map((combo, idx) => (
                                    <TableRow
                                        key={`${combo.pricingMatrixId}-${idx}`}
                                        hover
                                        selected={isSelected(combo)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell align="center">
                                            <Checkbox
                                                checked={isSelected(combo)}
                                                onChange={() => onSelectCombo(combo)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {combo.lensType && (
                                                <Chip
                                                    label={LENS_TYPE_MAP[combo.lensType]?.code || combo.lensType}
                                                    size="small"
                                                    color={LENS_TYPE_MAP[combo.lensType]?.color || 'default'}
                                                    variant="outlined"
                                                    sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>{combo.modelName}</TableCell>
                                        <TableCell>{combo.materialName}</TableCell>
                                        <TableCell>{combo.indexValue}</TableCell>
                                        <TableCell align="right">{formatAmountForDisplay(combo.baseRetailPrice)}</TableCell>
                                        <TableCell align="center">{combo.treatmentCount}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={(e, page) => onPageChange(page)}
                                size="small"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                        Showing {startIdx + 1}-{Math.min(startIdx + PAGE_SIZE, sortedCombos.length)} of {sortedCombos.length} combos
                    </Typography>
                </>
            )}
        </Box>
    );
}

export default function LensConfigurationDialog({ open, onClose, onSelect, selectedRx, existingLensData }) {
    const [manufacturers, setManufacturers] = useState([]);
    const [loading, setLoading] = useState(false);

    // All pricing combos loaded for each eye (from batch endpoint)
    const [allOdCombos, setAllOdCombos] = useState([]);
    const [allOsCombos, setAllOsCombos] = useState([]);

    // Grid filter and sort state for each eye
    const [odFilterText, setOdFilterText] = useState('');
    const [osFilterText, setOsFilterText] = useState('');
    const [odLensTypeFilter, setOdLensTypeFilter] = useState('');
    const [osLensTypeFilter, setOsLensTypeFilter] = useState('');

    // Pagination state for each eye
    const [odPage, setOdPage] = useState(1);
    const [osPage, setOsPage] = useState(1);

    // Separate configurations for each eye
    const [odConfig, setOdConfig] = useState({ ...INITIAL_EYE_CONFIG });
    const [osConfig, setOsConfig] = useState({ ...INITIAL_EYE_CONFIG });

    useEffect(() => {
        if (open) {
            loadReferenceData();
            
            if (existingLensData?.OD && existingLensData?.OS) {
                populateExistingData(existingLensData);
            } else {
                setOdConfig({ ...INITIAL_EYE_CONFIG });
                setOsConfig({ ...INITIAL_EYE_CONFIG });
                setAllOdCombos([]);
                setAllOsCombos([]);
                setOdFilterText('');
                setOsFilterText('');
                setOdLensTypeFilter('');
                setOsLensTypeFilter('');
            }
        }
    }, [open, existingLensData]);

    const loadReferenceData = async () => {
        try {
            const result = await ValidDataService.getManufacturersByType('lens');
            if (result.code === 0) setManufacturers(result.data || []);
        } catch (error) {
            if (isLocalDev) console.error('Failed to load reference data', error);
            toast.error('Failed to load lens configuration options');
        }
    };

    const populateExistingData = async (lensData) => {
        if (lensData.OD) {
            const odData = lensData.OD;
            if (odData.manufacturerId) {
                try {
                    const result = await LensConfigurationService.getPricingByManufacturer(odData.manufacturerId);
                    if (result.code === 0) {
                        setAllOdCombos(result.data || []);
                    }
                } catch (error) {
                    if (isLocalDev) console.error('Failed to load OD pricing combos', error);
                }
            }
            setOdConfig({
                manufacturerId: odData.manufacturerId || '',
                lensModelId: odData.lensModelId || '',
                lensMaterialId: odData.lensMaterialId || '',
                lensIndexId: odData.lensIndexId || '',
                treatmentIds: odData.treatmentIds || [],
                availableTreatments: odData.availableTreatments || [],
                pricing: odData.pricing || null,
                labInstructions: odData.labInstructions || '',
            });
        }

        if (lensData.OS) {
            const osData = lensData.OS;
            if (osData.manufacturerId) {
                try {
                    const result = await LensConfigurationService.getPricingByManufacturer(osData.manufacturerId);
                    if (result.code === 0) {
                        setAllOsCombos(result.data || []);
                    }
                } catch (error) {
                    if (isLocalDev) console.error('Failed to load OS pricing combos', error);
                }
            }
            setOsConfig({
                manufacturerId: osData.manufacturerId || '',
                lensModelId: osData.lensModelId || '',
                lensMaterialId: osData.lensMaterialId || '',
                lensIndexId: osData.lensIndexId || '',
                treatmentIds: osData.treatmentIds || [],
                availableTreatments: osData.availableTreatments || [],
                pricing: osData.pricing || null,
                labInstructions: osData.labInstructions || '',
            });
        }
    };

    const handleManufacturerChange = async (manufacturerId, eye) => {
        const updateConfig = eye === 'OD' ? setOdConfig : setOsConfig;
        const setCombos = eye === 'OD' ? setAllOdCombos : setAllOsCombos;
        const setFilter = eye === 'OD' ? setOdFilterText : setOsFilterText;
        const setLensTypeFilter = eye === 'OD' ? setOdLensTypeFilter : setOsLensTypeFilter;

        updateConfig(prev => ({
            ...prev,
            manufacturerId,
            lensModelId: '',
            lensMaterialId: '',
            lensIndexId: '',
            pricing: null,
            treatmentIds: [],
            availableTreatments: [],
        }));
        setFilter('');
        setLensTypeFilter('');
        const setPage = eye === 'OD' ? setOdPage : setOsPage;
        setPage(1);

        if (manufacturerId) {
            setLoading(true);
            try {
                const result = await LensConfigurationService.getPricingByManufacturer(manufacturerId);
                if (result.code === 0) {
                    setCombos(result.data || []);
                } else {
                    toast.warning('No lens configurations found for this manufacturer');
                    setCombos([]);
                }
            } catch (error) {
                if (isLocalDev) console.error('Failed to load pricing combos', error);
                toast.error('Failed to load lens options');
            } finally {
                setLoading(false);
            }
        } else {
            setCombos([]);
        }
    };

    const handleSelectCombo = async (combo, eye) => {
        const updateConfig = eye === 'OD' ? setOdConfig : setOsConfig;

        updateConfig(prev => ({
            ...prev,
            lensModelId: combo.lensModelId,
            lensMaterialId: combo.lensMaterialId,
            lensIndexId: combo.lensIndexId,
            pricing: null, // Will be recalculated by useEffect
            treatmentIds: [],
            availableTreatments: [],
        }));
    };

    const calculatePricing = async (config, eye) => {
        const { lensModelId, lensMaterialId, lensIndexId, treatmentIds } = config;

        if (!lensModelId || !lensMaterialId || !lensIndexId) {
            return;
        }

        setLoading(true);
        try {
            const result = await LensConfigurationService.getPricing({
                lensModelId,
                lensMaterialId,
                lensIndexId,
                treatmentIds: treatmentIds || []
            });
            if (result.code === 0) {
                const updateConfig = eye === 'OD' ? setOdConfig : setOsConfig;
                updateConfig(prev => ({
                    ...prev,
                    pricing: result.data,
                    availableTreatments: result.data?.availableTreatments || [],
                }));
            } else {
                const updateConfig = eye === 'OD' ? setOdConfig : setOsConfig;
                updateConfig(prev => ({
                    ...prev,
                    availableTreatments: [],
                    treatmentIds: [],
                }));
            }
        } catch (error) {
            if (isLocalDev) console.error('Failed to get pricing', error);
            toast.error('Failed to calculate lens pricing');
        } finally {
            setLoading(false);
        }
    };

    // Auto-calculate pricing when model/material/index changes
    useEffect(() => {
        if (odConfig.lensModelId && odConfig.lensMaterialId && odConfig.lensIndexId) {
            calculatePricing(odConfig, 'OD');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [odConfig.lensModelId, odConfig.lensMaterialId, odConfig.lensIndexId, odConfig.treatmentIds]);

    useEffect(() => {
        if (osConfig.lensModelId && osConfig.lensMaterialId && osConfig.lensIndexId) {
            calculatePricing(osConfig, 'OS');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [osConfig.lensModelId, osConfig.lensMaterialId, osConfig.lensIndexId, osConfig.treatmentIds]);

    const getSelectedCombo = (config, eye) => {
        const combos = eye === 'OD' ? allOdCombos : allOsCombos;
        return combos.find(c => 
            c.lensModelId === config.lensModelId &&
            c.lensMaterialId === config.lensMaterialId &&
            c.lensIndexId === config.lensIndexId
        );
    };

    const handleTreatmentChange = (treatmentId, isChecked, eye) => {
        const updateConfig = eye === 'OD' ? setOdConfig : setOsConfig;
        updateConfig(prev => ({
            ...prev,
            treatmentIds: isChecked
                ? [...prev.treatmentIds, treatmentId]
                : prev.treatmentIds.filter(id => id !== treatmentId),
        }));
    };

    const handleLabInstructionsChange = (value, eye) => {
        const updateConfig = eye === 'OD' ? setOdConfig : setOsConfig;
        updateConfig(prev => ({
            ...prev,
            labInstructions: value,
        }));
    };

    const handleInsertLabToken = (token, eye) => {
        const updateConfig = eye === 'OD' ? setOdConfig : setOsConfig;
        updateConfig(prev => {
            const existing = (prev.labInstructions || '').trim();
            if (!existing) {
                return { ...prev, labInstructions: token };
            }

            const alreadyIncluded = existing.toLowerCase().includes(token.toLowerCase());
            if (alreadyIncluded) {
                return prev;
            }

            return {
                ...prev,
                labInstructions: `${existing}, ${token}`,
            };
        });
    };

    const handleCopyConfig = (fromEye) => {
        if (fromEye === 'OD') {
            setOsConfig({ ...odConfig });
            setAllOsCombos([...allOdCombos]);
            setOsFilterText(odFilterText);
            setOsLensTypeFilter(odLensTypeFilter);
        } else {
            setOdConfig({ ...osConfig });
            setAllOdCombos([...allOsCombos]);
            setOdFilterText(osFilterText);
            setOdLensTypeFilter(osLensTypeFilter);
        }
    };

    const handleSave = () => {
        // Validate OD
        if (!odConfig.lensModelId || !odConfig.lensMaterialId || !odConfig.lensIndexId) {
            toast.error('Please complete the lens configuration for OD (Right Eye)');
            return;
        }

        if (!odConfig.pricing) {
            toast.error('Pricing not available for OD. Please check your configuration.');
            return;
        }

        // Validate OS
        if (!osConfig.lensModelId || !osConfig.lensMaterialId || !osConfig.lensIndexId) {
            toast.error('Please complete the lens configuration for OS (Left Eye)');
            return;
        }
        if (!osConfig.pricing) {
            toast.error('Pricing not available for OS. Please check your configuration.');
            return;
        }

        // Get full combo objects for context
        const odCombo = getSelectedCombo(odConfig, 'OD');
        const osCombo = getSelectedCombo(osConfig, 'OS');

        const lensConfigurations = {
            OD: {
                ...odConfig,
                manufacturer: manufacturers.find(m => m.id === odConfig.manufacturerId),
                model: odCombo?.lensModel || null,
                material: odCombo?.lensMaterial || null,
                index: odCombo?.lensIndex || null,
                treatments: odConfig.availableTreatments.filter(t => odConfig.treatmentIds.includes(t.id)),
                rx: selectedRx?.OD || null,
            },
            OS: {
                ...osConfig,
                manufacturer: manufacturers.find(m => m.id === osConfig.manufacturerId),
                model: osCombo?.lensModel || null,
                material: osCombo?.lensMaterial || null,
                index: osCombo?.lensIndex || null,
                treatments: osConfig.availableTreatments.filter(t => osConfig.treatmentIds.includes(t.id)),
                rx: selectedRx?.OS || null,
            },
        };

        onSelect(lensConfigurations);
        onClose();
    };

    const renderEyeConfiguration = (eye, config, setConfig, allCombos) => {
        const selectedCombo = getSelectedCombo(config, eye);
        const filterText = eye === 'OD' ? odFilterText : osFilterText;
        const setFilterText = eye === 'OD' ? setOdFilterText : setOsFilterText;
        const lensTypeFilter = eye === 'OD' ? odLensTypeFilter : osLensTypeFilter;
        const setLensTypeFilter = eye === 'OD' ? setOdLensTypeFilter : setOsLensTypeFilter;

        return (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {eye === 'OD' ? 'OD - Right Eye' : 'OS - Left Eye'}
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleCopyConfig(eye)}
                        disabled={!selectedCombo}
                    >
                        Copy to {eye === 'OD' ? 'OS' : 'OD'}
                    </Button>
                </Box>

                {/* Manufacturer Selection */}
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Manufacturer</InputLabel>
                    <Select
                        value={config.manufacturerId}
                        onChange={(e) => handleManufacturerChange(e.target.value, eye)}
                        label="Manufacturer"
                    >
                        <MenuItem value="">Select Manufacturer</MenuItem>
                        {manufacturers.map(mfg => (
                            <MenuItem key={mfg.id} value={mfg.id}>{mfg.name}</MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>Select manufacturer to view available lens options</FormHelperText>
                </FormControl>

                {/* Pricing Matrix Grid */}
                {config.manufacturerId && (
                    <Box sx={{ mb: 2 }}>
                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}
                        <Box sx={{ position: 'relative' }}>
                            <LensPricingMatrix
                                combos={allCombos}
                                selectedCombo={selectedCombo}
                                onSelectCombo={(combo) => handleSelectCombo(combo, eye)}
                                filterText={filterText}
                                onFilterChange={setFilterText}
                                lensTypeFilter={lensTypeFilter}
                                onLensTypeFilterChange={setLensTypeFilter}
                                sortBy={{}}
                                onSortChange={() => {}}
                                currentPage={eye === 'OD' ? odPage : osPage}
                                onPageChange={eye === 'OD' ? setOdPage : setOsPage}
                            />
                            {loading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(255,255,255,0.35)',
                                        pointerEvents: 'none',
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                )}

                {/* Treatment Selection */}
                {selectedCombo && config.availableTreatments.length > 0 && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Available Treatments ({config.availableTreatments.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {config.availableTreatments.map(treatment => (
                                <FormControlLabel
                                    key={treatment.id}
                                    control={
                                        <Checkbox
                                            checked={config.treatmentIds.includes(treatment.id)}
                                            onChange={(e) => handleTreatmentChange(treatment.id, e.target.checked, eye)}
                                        />
                                    }
                                    label={`${treatment.name} (+${formatAmountForDisplay(treatment.retailPrice)})`}
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Pricing Display */}
                {config.pricing && (
                    <Box sx={{ p: 2, bgcolor: '#f0f0f0', borderRadius: 1, mb: 2 }}>
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <Typography variant="body2">Base Retail:</Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {formatAmountForDisplay(config.pricing.baseRetailPrice)}
                                </Typography>
                            </Grid>
                            {config.pricing.treatments && config.pricing.treatments.length > 0 && (
                                <>
                                    <Grid item xs={6}>
                                        <Typography variant="body2">Treatments Add-on:</Typography>
                                    </Grid>
                                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            +{formatAmountForDisplay(
                                                config.pricing.treatments.reduce((sum, t) => sum + parseFloat(t.additionalRetailPrice || 0), 0)
                                            )}
                                        </Typography>
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12}><Divider /></Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Total Retail:</Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: Colors.primary }}>
                                    {formatAmountForDisplay(config.pricing.totalRetail)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Lab Instructions */}
                {selectedCombo && (
                    <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                            Quick tokens
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                            {LAB_INSTRUCTION_TOKENS.map((token) => (
                                <Chip
                                    key={`${eye}-${token}`}
                                    label={token}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleInsertLabToken(token, eye)}
                                />
                            ))}
                        </Box>
                        <TextField
                            fullWidth
                            size="small"
                            label="Lab Instructions (tint, edge, etc.)"
                            multiline
                            rows={2}
                            value={config.labInstructions}
                            onChange={(e) => handleLabInstructionsChange(e.target.value, eye)}
                            placeholder="Enter any special instructions for the lab..."
                        />
                    </Box>
                )}
            </Box>
        );
    };

    const grandTotal = useMemo(() => {
        const od = odConfig.pricing?.totalRetail || 0;
        const os = osConfig.pricing?.totalRetail || 0;
        return parseFloat(od) + parseFloat(os);
    }, [odConfig.pricing, osConfig.pricing]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
            <AppDialogTitle 
                title={existingLensData?.OD ? "Edit Lens Configuration" : "Configure Lens"} 
                onClose={onClose} 
            />
            <DialogContent>
                {selectedRx && (
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                        Rx selected: OD SPH {selectedRx.OD?.sphere || '—'}, CYL {selectedRx.OD?.cylinder || '—'} | 
                        OS SPH {selectedRx.OS?.sphere || '—'}, CYL {selectedRx.OS?.cylinder || '—'}
                    </Alert>
                )}

                <Paper sx={{ mb: 2, p: 1.5, bgcolor: '#111', color: '#fff', borderRadius: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                        Grand Total (OD + OS): {formatAmountForDisplay(grandTotal)}
                    </Typography>
                </Paper>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        {renderEyeConfiguration('OD', odConfig, setOdConfig, allOdCombos)}
                    </Grid>

                    <Grid item xs={12} md={6}>
                        {renderEyeConfiguration('OS', osConfig, setOsConfig, allOsCombos)}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleSave} variant="contained" disabled={loading}>
                    {existingLensData?.OD ? 'Update' : 'Add to Invoice'}
                </Button>
                <Button onClick={onClose} variant="outlined" color="cancel">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
